import { type PointerEvent as ReactPointerEvent, useCallback, useState } from "react";
import { attachHorizontalDrag } from "../utils/horizontal-drag";
import { readStoredPct, writeStoredPct } from "../utils/resize-persistence";

interface ResizableWidth {
	/** Current width as a percentage of the viewport. */
	widthPct: number;
	/** pointerdown handler for a left-edge resize handle. */
	startResize: (e: ReactPointerEvent) => void;
}

/**
 * Width state for a right-anchored panel resized by dragging its left edge.
 * Width is a % of the viewport, clamped to [minPct, maxPct]. When `storageKey`
 * is given the width persists to localStorage so it survives reopening.
 */
export const useResizableWidth = (
	defaultPct = 80,
	minPct = 40,
	maxPct = 100,
	storageKey?: string
): ResizableWidth => {
	const [widthPct, setWidthPct] = useState(() => {
		const initial = storageKey ? readStoredPct(storageKey, defaultPct) : defaultPct;
		// Clamp a previously-stored width into the current bounds.
		return Math.min(maxPct, Math.max(minPct, initial));
	});
	const startResize = useCallback(
		(e: ReactPointerEvent) => {
			e.preventDefault();
			attachHorizontalDrag((clientX) => {
				const pct = ((window.innerWidth - clientX) / window.innerWidth) * 100;
				const clamped = Math.min(maxPct, Math.max(minPct, pct));
				setWidthPct(clamped);
				if (storageKey) writeStoredPct(storageKey, clamped);
			}, "ew-resize");
		},
		[minPct, maxPct, storageKey]
	);
	return { widthPct, startResize };
};
